import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Trash2, Sparkles, Loader2 } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { streamPost } from "../lib/stream";

let seq = 0;
const newId = () => `m${Date.now()}-${seq++}`;
const withIds = (msgs) => msgs.map((m) => ({ ...m, id: newId() }));

const SUGGERIMENTI = [
  "Quali garanzie scadono nei prossimi 30 giorni?",
  "Garanzia legale su auto usata: posso ridurla a 12 mesi?",
  "Entro quanto il cliente deve denunciare un difetto di conformità?",
  "Come proporre un'estensione Gold a un cliente con garanzia in scadenza?",
];

const renderBold = (text) =>
  text.split(/(\*\*[^*]+\*\*)/g).map((p, k) => (p.startsWith("**") && p.endsWith("**") ? <b key={`${k}-${p}`}>{p.slice(2, -2)}</b> : p));

export const Bubble = ({ m, i }) => (
  <div className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${i}`}>
    <div className={`max-w-[85%] whitespace-pre-wrap rounded-lg px-4 py-3 text-sm leading-relaxed ${m.role === "user" ? "bg-[#B08D57] text-white" : "border bg-white"}`}>
      {m.content ? renderBold(m.content) : <Loader2 className="h-4 w-4 animate-spin" />}
    </div>
  </div>
);

export const Suggerimenti = ({ onPick }) => (
  <div className="mx-auto max-w-lg pt-10 text-center">
    <Sparkles className="mx-auto mb-3 h-8 w-8 text-[#B08D57]" />
    <div className="font-display text-xl font-bold">Come posso aiutarti?</div>
    <div className="mt-6 grid gap-2">
      {SUGGERIMENTI.map((s, i) => (
        <button key={s} onClick={() => onPick(s)} className="rounded-md border bg-white px-4 py-2.5 text-left text-sm transition-colors hover:border-[#B08D57]" data-testid={`chat-suggestion-${i}`}>{s}</button>
      ))}
    </div>
  </div>
);

export const ChatSidebar = ({ sessions, sid, onOpen, onRemove }) => (
  <aside className="hidden rounded-md border bg-white lg:block" data-testid="chat-sessions">
    <div className="border-b px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Conversazioni</div>
    {sessions.length === 0 && <div className="px-4 py-6 text-sm text-muted-foreground">Nessuna conversazione</div>}
    {sessions.map((s) => (
      <div key={s.id} className={`group flex items-center border-b last:border-0 ${s.id === sid ? "bg-[#F4EDE1]" : "hover:bg-slate-50"}`}>
        <button onClick={() => onOpen(s.id)} className="min-w-0 flex-1 truncate px-4 py-3 text-left text-sm" data-testid={`chat-session-${s.id}`}>{s.title}</button>
        <button onClick={() => onRemove(s.id)} className="px-3 opacity-0 group-hover:opacity-100" data-testid={`chat-session-delete-${s.id}`}><Trash2 className="h-4 w-4 text-red-600" /></button>
      </div>
    ))}
  </aside>
);

export function useChat() {
  const [sessions, setSessions] = useState([]);
  const [sid, setSid] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);

  const loadSessions = useCallback(() => api.get("/ai/chat").then((r) => setSessions(r.data)), []);
  useEffect(() => { loadSessions(); }, [loadSessions]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const reset = () => { setSid(null); setMessages([]); };
  const open = async (id) => { setSid(id); const { data } = await api.get(`/ai/chat/${id}`); setMessages(withIds(data.messages)); };
  const remove = async (id) => {
    try { await api.delete(`/ai/chat/${id}`); if (id === sid) reset(); loadSessions(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const send = async (msg) => {
    if (!msg || loading) return;
    setLoading(true);
    setMessages((p) => [...p, { id: newId(), role: "user", content: msg }, { id: newId(), role: "assistant", content: "" }]);
    const append = (t) => setMessages((p) => [...p.slice(0, -1), { ...p[p.length - 1], content: p[p.length - 1].content + t }]);
    try {
      await streamPost("/ai/chat", { session_id: sid, message: msg }, (ev) => {
        if (ev.session_id) setSid(ev.session_id);
        if (ev.t) append(ev.t);
      });
      loadSessions();
    } catch (e) {
      toast.error(e.message);
      setMessages((p) => p.slice(0, -1));
    } finally { setLoading(false); }
  };

  return { sessions, sid, messages, loading, endRef, reset, open, remove, send };
}
